import React from "react";
import { useStaticURL } from "tsi-common-react/src/utils/urls";
import { t } from "ttag";

import { Svg, TooltipIcon } from "../svg";

/**
 * Shared building blocks for the per-product spec-table partials
 * (``cms/blocks/product-specifications/<name>.html``), faithfully reproducing
 * the legacy Django markup so the existing ``.adapt-specifications`` /
 * ``.breeze-specifications`` CSS keeps working (#33410).
 *
 * Size-icon ``<img>`` ``src`` values are built from the reactivated
 * ``STATIC_URL`` context (``useStaticURL`` via ``SizeImg``): the legacy
 * ``{% static_image %}`` renders the EAGER plain-``<img>`` branch under puppeteer
 * (its ``_is_puppeteer_test`` check) with a ``{% static %}`` URL, and because
 * the project's ``StaticFilesStorage`` disables per-file hashing,
 * ``${STATIC_URL}${path}`` is byte-identical to that output. This is SSR-safe,
 * unlike a Vite asset ``import`` (which resolves only against the client bundle
 * and breaks during reactivated SSR). ``sizeIcons`` holds the static-relative
 * paths (under ``img/icons/product-specifications-block/``).
 */
export const sizeIcons = {
    adaptTwin: "img/icons/product-specifications-block/adapt_twin.png",
    adaptTwinXl: "img/icons/product-specifications-block/adapt_twin_xl.png",
    adaptDouble: "img/icons/product-specifications-block/adapt_double.png",
    adaptQueen: "img/icons/product-specifications-block/adapt_queen.png",
    adaptKing: "img/icons/product-specifications-block/adapt_king.png",
    adaptSplitKing:
        "img/icons/product-specifications-block/adapt_split_king.png",
    adaptSplitHeadKing:
        "img/icons/product-specifications-block/adapt_split_head_king.png",
    adaptCaKing: "img/icons/product-specifications-block/adapt_ca_king.png",
    adaptSplitCaKing:
        "img/icons/product-specifications-block/adapt_split_ca_king.png",
} as const;

/**
 * A size-icon ``<img>`` whose ``src`` is the static-relative ``icon`` path
 * resolved against the reactivated ``STATIC_URL`` context. Decorative: empty
 * ``alt`` (size name / dimensions are in adjacent text).
 */
export const SizeImg = (
    props: Readonly<{ icon: string }>,
): React.JSX.Element => {
    const staticURL = useStaticURL();
    return (
        <img
            src={`${staticURL}${props.icon}`}
            alt=""
            loading="lazy"
            decoding="async"
        />
    );
};

/**
 * Split-size tooltip trigger. ``initTooltips`` hydrates ``[data-tooltip]`` into
 * the shared auto-tooltip pattern (tabindex=0, sr-only name, aria-hidden icon,
 * no role=button). Do not put ``data-tip`` or ``title`` here: ReactTooltip
 * stamps ``role="button"`` on every ``[data-tip]``, which Allyant rejects for
 * hover/focus tooltips (A360 #350 / Planio #32085).
 *
 * ``TooltipContent`` stays a sibling AFTER the ``<h5>`` (direct child of the
 * cell) so SSR still matches the legacy partial nesting for CSS (#33410).
 */
export const TooltipTrigger = (props: {
    text: string;
    srText: string;
}): React.JSX.Element => (
    <span data-tooltip={props.text} data-tooltip-sr={props.srText}>
        <Svg
            Icon={TooltipIcon}
            src="tooltip-icon"
            className="tooltip__handle"
            ariaHidden
            description={props.srText}
        />
    </span>
);

export const TooltipContent = (props: { text: string }): React.JSX.Element => (
    <div
        className="__react_component_tooltip place-top type-dark tooltip__content"
        id="tooltip-0"
        role="tooltip"
        data-id="tooltip"
        style={{ left: "754px", top: "319px" }}
    >
        {props.text}
    </div>
);

const SPLIT_KING_TIP =
    "Two individual mattresses that allow for independent head and foot movements with a Split King adjustable base";
const SPLIT_CA_KING_TIP =
    "Two individual mattresses that allow for independent head and foot movements with a Split California King adjustable base";

/**
 * A plain (no-tooltip) size cell: icon ``<img>``, a sizing ``<h5>`` heading, and
 * the dimension/mattress/weight ``<p>`` lines.
 */
export const SizeCell = (
    props: Readonly<{
        icon: string;
        heading: string;
        lines: readonly string[];
    }>,
): React.JSX.Element => (
    <div>
        <SizeImg icon={props.icon} />
        <h5 className="product-specifications-block__size-heading">
            {props.heading}
        </h5>
        {props.lines.map((line, i) => (
            <p key={i}>{line}</p>
        ))}
    </div>
);

/**
 * The split-king size cell with the shared "Split King" tooltip. The
 * ``.__react_component_tooltip`` content div is a sibling AFTER the ``<h5>`` (a
 * direct child of the cell ``<div>``), matching the legacy partial nesting.
 */
export const SplitKingCell = (
    props: Readonly<{ lines: readonly string[] }>,
): React.JSX.Element => (
    <div>
        <SizeImg icon={sizeIcons.adaptSplitKing} />
        <h5 className="product-specifications-block__size-heading">
            Split King
            <TooltipTrigger
                text={SPLIT_KING_TIP}
                srText={t`Split King size details`}
            />
        </h5>
        <TooltipContent text={SPLIT_KING_TIP} />
        {props.lines.map((line, i) => (
            <p key={i}>{line}</p>
        ))}
    </div>
);

/**
 * The split-CA-king size cell with the shared "Split CA King" tooltip. The
 * ``.__react_component_tooltip`` content div is a sibling AFTER the ``<h5>``,
 * matching the legacy partial nesting.
 */
export const SplitCaKingCell = (
    props: Readonly<{ lines: readonly string[] }>,
): React.JSX.Element => (
    <div>
        <SizeImg icon={sizeIcons.adaptSplitCaKing} />
        <h5 className="product-specifications-block__size-heading">
            Split CA King
            <TooltipTrigger
                text={SPLIT_CA_KING_TIP}
                srText={t`Split CA King size details`}
            />
        </h5>
        <TooltipContent text={SPLIT_CA_KING_TIP} />
        {props.lines.map((line, i) => (
            <p key={i}>{line}</p>
        ))}
    </div>
);

/** Footnote line shared by all spec tables. */
export const FootNote = (): React.JSX.Element => (
    <p>* Dimensions of each individual mattress may vary</p>
);
